import type { Difficulty, MCQQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

/**
 * N-3-1：萬以內的數——整數量感（1~10000）。
 * 三上第一章：認識千位數，比大小，數線定位。
 *
 * difficulty 1：100–999（三位數比較與排列）
 * difficulty 2：1000–5000（四位數，千位為主）
 * difficulty 3：1000–10000（跨千位比較，含 10000）
 */
export function generateCount10000(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  let correct: number;
  let a: number, b: number, c: number;

  if (difficulty === 1) {
    // Pick a target 3-digit number; ask "which is largest/smallest"
    correct = randInt(rand, 100, 999);
    a = randInt(rand, 100, 999);
    b = randInt(rand, 100, 999);
    c = randInt(rand, 100, 999);
  } else if (difficulty === 2) {
    correct = randInt(rand, 1000, 5000);
    a = randInt(rand, 1000, 5000);
    b = randInt(rand, 1000, 5000);
    c = randInt(rand, 1000, 5000);
  } else {
    correct = randInt(rand, 1000, 10000);
    a = randInt(rand, 1000, 10000);
    b = randInt(rand, 1000, 10000);
    c = randInt(rand, 1000, 10000);
  }

  // Make `correct` the largest; build a "find the largest" question
  const nums = [a, b, c].map(n => {
    // ensure no duplicate equals correct
    if (n === correct) return correct + 1 <= (difficulty === 1 ? 999 : 10000) ? correct + 1 : correct - 1;
    return n;
  });
  const largest = Math.max(correct, ...nums);
  // Rebuild so `correct` wins
  const finalCorrect = largest;
  // Put finalCorrect in a random slot
  const allNums = [nums[0]!, nums[1]!, nums[2]!];
  // Replace random element with finalCorrect if it isn't already present
  if (!allNums.includes(finalCorrect)) {
    const replaceIdx = randInt(rand, 0, 2);
    allNums[replaceIdx] = finalCorrect + (rand() > 0.5 ? -randInt(rand, 1, 50) : randInt(rand, 1, 50));
    // Ensure allNums[replaceIdx] != finalCorrect after perturbation
  }
  // Add finalCorrect as the correct option
  const options = [...allNums, finalCorrect];
  // Shuffle
  const shuffled = [...options].sort((x, y) => mulberry32(seed + x)() - mulberry32(seed + y)());
  const correctIdx = shuffled.indexOf(finalCorrect);

  return {
    kind: 'mcq',
    prompt: `下面哪個數最大？`,
    options: shuffled,
    correctAnswer: correctIdx,
    explanation: `${shuffled.join('、')} 中，${finalCorrect} 最大。`,
    kp: 'math.number.10000',
  };
}
